import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Loader2, X, Check, Sparkles, AlertCircle, Stethoscope, Activity, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MS = 3 * 60 * 1000;

/**
 * TriageDictationButton — preview-first triage capture.
 *
 * Flow (mirrors Full Visit Dictation + section preview):
 *   1. Mic recording with live partial bubble (Web Speech API).
 *   2. On stop → preview modal opens immediately:
 *      • Stage A `/api/transcribe` (no section_type) → raw English
 *        conversation in ~1 s. Auto-translates Tamil / Telugu / Hindi
 *        / English audio to English via the same `/transcribe` route
 *        (auto-detect + Claude-translate fallback wired backend-side).
 *      • Stage B `/api/clinic-ops/voice/parse-vitals` → structured
 *        {vitals, chief_complaint, symptoms} parsed from the raw
 *        conversation in ~0.3 s (no LLM, regex-based).
 *   3. Modal shows the FULL conversation transcript (top, editable
 *      for accuracy) AND the EXTRACTED symptoms / vitals (below,
 *      doctor-friendly chips + form fields). Doctor confirms or
 *      tweaks, then "Apply to Triage" pushes the structured payload
 *      back to the parent via `onParsed`.
 *
 * Per the product brief: "capture the conversation content AND record
 * just the symptoms" — both panels stay visible so the receptionist
 * has full audit visibility while only the extracted symptoms feed
 * the doctor's view.
 */
export default function TriageDictationButton({ onParsed, onTranscript, label = 'Dictate triage', size = 'md', className = '' }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [livePartial, setLivePartial] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [rawText, setRawText] = useState('');
  const [editedTranscript, setEditedTranscript] = useState('');
  const [extracted, setExtracted] = useState({ vitals: {}, chief_complaint: '', symptoms: [] });

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const speechRecRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (speechRecRef.current) {
      try { speechRecRef.current.stop(); } catch (_) { /* ignore */ }
      speechRecRef.current = null;
    }
    recorderRef.current = null;
  }, []);
  useEffect(() => () => cleanup(), [cleanup]);

  const startLivePreview = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      // Multi-language hint — `en-IN` decodes English + Hinglish well;
      // we let the server-side Whisper auto-detect for full multilingual
      // accuracy. The live bubble is *preview only*; the real transcript
      // comes from Whisper.
      rec.lang = 'en-IN';
      rec.onresult = (e) => {
        let live = '';
        for (let i = e.resultIndex; i < e.results.length; i++) live += e.results[i][0].transcript;
        setLivePartial((prev) => (prev + ' ' + live).trim().slice(-400));
      };
      rec.onerror = () => {};
      rec.start();
      speechRecRef.current = rec;
    } catch (_) { /* ignore */ }
  }, []);

  const startRecording = async () => {
    setElapsed(0); setLivePartial(''); chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;
      let mime = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/webm';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        setRecording(false);
        if (blob.size < 1000) {
          toast.info('Recording was too short. Speak for at least a few seconds.');
          return;
        }
        await sendForProcessing(blob, mime);
      };
      recorder.start(250);
      setRecording(true);
      startLivePreview();
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === 'recording') {
          recorderRef.current.stop();
          toast.info('Auto-stopped at 3 minutes.');
        }
      }, MAX_MS);
    } catch (err) {
      console.error(err);
      toast.error('Microphone access denied. Please allow microphone in browser settings.');
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const sendForProcessing = async (blob, mime) => {
    setUploading(true);
    setPreviewOpen(true);
    setRawText('');
    setEditedTranscript('');
    setExtracted({ vitals: {}, chief_complaint: '', symptoms: [] });
    try {
      // Stage A — Whisper (auto-language, translates to English server-side).
      const fd = new FormData();
      fd.append('audio', blob, mime.includes('mp4') ? 'voice.mp4' : 'voice.webm');
      const tr = await axios.post(`${API_URL}/api/transcribe`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const raw = (tr.data?.text || tr.data?.raw_text || '').trim();
      if (!raw) {
        toast.warning('Could not detect any speech. Please try again.');
        setPreviewOpen(false);
        return;
      }
      setRawText(raw);
      setEditedTranscript(raw);
      setUploading(false);
      onTranscript?.(raw);

      // Stage B — Parse the transcript into structured vitals + symptoms.
      setParsing(true);
      try {
        const ex = await axios.post(`${API_URL}/api/clinic-ops/voice/parse-vitals`,
          { transcript: raw },
          { withCredentials: true });
        const v = ex.data?.vitals || {};
        // Convert °C → °F if needed (parser emits °C, UI expects °F).
        if (v.temperature != null) {
          const t = Number(v.temperature);
          v.temperature = t < 50 ? Number((t * 1.8 + 32).toFixed(1)) : t;
        }
        setExtracted({
          vitals: v,
          chief_complaint: ex.data?.chief_complaint || '',
          symptoms: ex.data?.symptoms || [],
        });
      } finally {
        setParsing(false);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Triage capture failed. Please try again.');
      setPreviewOpen(false);
    } finally {
      setUploading(false);
    }
  };

  const handleApply = () => {
    // Bubble back the same shape `/parse-vitals` would have returned —
    // parent's onParsed handler stays unchanged.
    onParsed?.({
      vitals: extracted.vitals,
      chief_complaint: extracted.chief_complaint || (extracted.symptoms || []).join(', '),
      symptoms: extracted.symptoms,
      transcript: editedTranscript,
    });
    setPreviewOpen(false);
    toast.success('Triage applied — review and save when ready');
  };

  const onClickMic = () => {
    if (uploading || parsing) return;
    if (recording) stopRecording();
    else startRecording();
  };

  const processing = uploading || parsing;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <>
      <Button
        type="button"
        size={size}
        onClick={onClickMic}
        disabled={processing && !previewOpen}
        className={`gap-2 ${
          recording
            ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
            : processing
              ? 'bg-violet-400 text-white'
              : 'bg-violet-600 hover:bg-violet-700 text-white'
        } ${className}`}
        data-testid="triage-dictate-btn"
      >
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> :
          recording ? <Square className="h-4 w-4 fill-current" /> :
          <Mic className="h-4 w-4" />}
        {recording ? 'Stop & preview' : label}
      </Button>

      {recording && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] max-w-xl w-[calc(100%-3rem)] bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl px-5 py-3 border border-white/10"
          data-testid="triage-live-partial"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">
              Listening · {mm}:{ss} / 03:00 · Triage
              <span className="ml-1.5 text-white/40">· Tamil / Telugu / Hindi / English OK</span>
            </span>
            <button
              onClick={stopRecording}
              className="ml-auto text-[10px] underline text-white/70 hover:text-white"
              data-testid="triage-stop-link"
            >
              Stop & preview
            </button>
          </div>
          <p className="text-sm leading-snug text-white/95 line-clamp-3">
            {livePartial || <span className="italic text-white/40">Start speaking… your words will appear here in real time.</span>}
          </p>
        </div>
      )}

      <TriagePreviewModal
        open={previewOpen}
        uploading={uploading}
        parsing={parsing}
        rawText={rawText}
        editedTranscript={editedTranscript}
        setEditedTranscript={setEditedTranscript}
        extracted={extracted}
        setExtracted={setExtracted}
        onClose={() => setPreviewOpen(false)}
        onApply={handleApply}
      />
    </>
  );
}

// ===================== Triage Preview Modal =====================

function TriagePreviewModal({
  open, uploading, parsing,
  rawText, editedTranscript, setEditedTranscript,
  extracted, setExtracted,
  onClose, onApply,
}) {
  if (!open) return null;

  const v = extracted.vitals || {};
  const setV = (k, val) => setExtracted((prev) => ({
    ...prev,
    vitals: { ...prev.vitals, [k]: val === '' ? undefined : (isNaN(+val) ? val : +val) },
  }));
  const removeSymptom = (i) => setExtracted((prev) => ({ ...prev, symptoms: prev.symptoms.filter((_, idx) => idx !== i) }));
  const addSymptom = (s) => {
    const trimmed = (s || '').trim();
    if (!trimmed) return;
    setExtracted((prev) => ({ ...prev, symptoms: [...(prev.symptoms || []), trimmed] }));
  };

  const vitalFields = [
    { k: 'bp_systolic',  label: 'BP Sys',  unit: 'mmHg' },
    { k: 'bp_diastolic', label: 'BP Dia',  unit: 'mmHg' },
    { k: 'pulse',        label: 'Pulse',   unit: 'bpm'  },
    { k: 'spo2',         label: 'SpO₂',    unit: '%'    },
    { k: 'temperature',  label: 'Temp',    unit: '°F'   },
    { k: 'weight',       label: 'Weight',  unit: 'kg'   },
    { k: 'height',       label: 'Height',  unit: 'cm'   },
    { k: 'rbs',          label: 'RBS',     unit: 'mg/dL'},
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden border-0 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="triage-preview-modal"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-violet-700 via-fuchsia-700 to-pink-600 text-white px-6 pt-4 pb-3 flex-shrink-0">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-pink-300/20 rounded-full blur-3xl" />
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Stethoscope className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>Triage · Dictation Preview</h3>
                <p className="text-[11px] text-white/75">Conversation captured below. Symptoms + vitals extracted on the right. Edit anything before applying.</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="triage-preview-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status strip */}
        <div className="px-6 py-2 bg-slate-50 border-b flex items-center gap-3 text-[11px]">
          {uploading ? (
            <div className="flex items-center gap-1.5 text-violet-700 font-semibold"><Loader2 className="h-3 w-3 animate-spin" /> Transcribing audio…</div>
          ) : (
            <div className="flex items-center gap-1.5 text-emerald-700 font-semibold"><Check className="h-3 w-3" /> Conversation captured</div>
          )}
          {parsing ? (
            <div className="flex items-center gap-1.5 text-fuchsia-700 font-semibold"><Sparkles className="h-3 w-3 animate-pulse" /> Extracting symptoms + vitals…</div>
          ) : rawText ? (
            <div className="flex items-center gap-1.5 text-fuchsia-700 font-semibold"><Sparkles className="h-3 w-3" /> Extracted {extracted.symptoms?.length || 0} symptom(s) + {Object.values(v).filter((x) => x != null && x !== '').length} vital(s)</div>
          ) : null}
        </div>

        {/* Body — split conversation (left) + extracted (right) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {uploading && !rawText ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin mb-3 text-violet-500" />
              <p className="text-sm">Listening to your voice…</p>
              <p className="text-[11px] mt-1">Typically takes ~1 second</p>
            </div>
          ) : !rawText ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <AlertCircle className="h-8 w-8 mb-2 text-slate-300" />
              <p className="text-sm italic">No transcript yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* LEFT — full conversation */}
              <div className="p-5 border-r border-slate-200 bg-slate-50/40">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2 flex items-center gap-1.5">
                  <ClipboardList className="h-3 w-3" /> Conversation
                </div>
                <Textarea
                  value={editedTranscript}
                  onChange={(e) => setEditedTranscript(e.target.value)}
                  placeholder="Conversation transcript…"
                  className="min-h-[260px] text-sm leading-relaxed bg-white border-slate-200 focus-visible:ring-violet-500"
                  data-testid="triage-preview-transcript"
                />
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  Auto-translated to English from Tamil / Telugu / Hindi if needed. Verbatim view — full audit.
                </p>
              </div>

              {/* RIGHT — extracted symptoms + vitals */}
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2 flex items-center gap-1.5">
                  <Activity className="h-3 w-3" /> Symptoms (this is what doctor sees)
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3 min-h-[36px] p-2 bg-fuchsia-50 border border-fuchsia-200 rounded-lg">
                  {(extracted.symptoms || []).length === 0 ? (
                    <span className="text-[11px] text-slate-400 italic">No symptoms extracted yet…</span>
                  ) : (extracted.symptoms || []).map((s, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-white text-fuchsia-700 border-fuchsia-300 gap-1 cursor-pointer hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition"
                      onClick={() => removeSymptom(i)}
                      title="Click to remove"
                      data-testid={`triage-preview-symptom-${i}`}
                    >
                      {s}<X className="h-2.5 w-2.5" />
                    </Badge>
                  ))}
                </div>
                <SymptomAdder onAdd={addSymptom} />

                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mt-4 mb-2 flex items-center gap-1.5">
                  <Activity className="h-3 w-3" /> Vitals (editable)
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {vitalFields.map((f) => (
                    <label key={f.k} className="flex flex-col gap-0.5">
                      <span className="text-[9px] uppercase font-bold text-slate-500">{f.label}</span>
                      <div className="flex items-center gap-1">
                        <Input
                          value={v[f.k] ?? ''}
                          onChange={(e) => setV(f.k, e.target.value)}
                          placeholder={f.unit}
                          className="h-8 text-xs"
                          data-testid={`triage-preview-v-${f.k}`}
                        />
                        <span className="text-[10px] text-slate-400">{f.unit}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex items-center justify-between bg-white flex-shrink-0">
          <Button variant="ghost" onClick={onClose} className="gap-1.5 text-slate-600" data-testid="triage-preview-cancel">
            <X className="h-4 w-4" /> Discard
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
              {(extracted.symptoms || []).length} symptoms · {Object.values(v).filter((x) => x != null && x !== '').length} vitals
            </Badge>
            <Button
              onClick={onApply}
              disabled={!rawText || uploading}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white gap-1.5 px-5"
              data-testid="triage-preview-apply"
            >
              <Check className="h-4 w-4" />
              Apply to Triage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SymptomAdder({ onAdd }) {
  const [val, setVal] = useState('');
  return (
    <div className="flex gap-1 mt-2">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Add a missed symptom (e.g. nausea)"
        className="h-8 text-xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onAdd(val); setVal(''); }
        }}
        data-testid="triage-preview-add-symptom-input"
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 px-2 text-[11px]"
        onClick={() => { onAdd(val); setVal(''); }}
        data-testid="triage-preview-add-symptom-btn"
      >
        Add
      </Button>
    </div>
  );
}
